import { Controller } from "@hotwired/stimulus"
import i18n from "../javascripts/util/i18n"

// Document-tab state classes — kept identical to the server-rendered set in
// _questionary_shell.html.erb so switching documents stays consistent.
const TAB_CARD = {
  active: ["border-brand-500", "bg-brand-50", "dark:border-brand-500", "dark:bg-brand-500/10"],
  complete: ["border-success-500", "bg-success-50", "dark:border-success-500", "dark:bg-success-500/10"],
  inactive: ["border-gray-200", "bg-white", "hover:bg-gray-50", "dark:border-gray-700", "dark:bg-gray-900", "dark:hover:bg-gray-800"]
}
const TAB_ICON = {
  active: ["bg-brand-500", "text-white"],
  complete: ["bg-success-500", "text-white"],
  inactive: ["bg-gray-100", "text-gray-400", "dark:bg-gray-800", "dark:text-gray-500"]
}
const TAB_LABEL = {
  active: ["text-brand-700", "dark:text-brand-300"],
  inactive: ["text-gray-800", "dark:text-gray-100"]
}
const TAB_COUNT = {
  active: ["text-brand-600", "dark:text-brand-400"],
  complete: ["text-success-600", "dark:text-success-400"],
  inactive: ["text-gray-400", "dark:text-gray-500"]
}

export default class extends Controller {
  static values = {
    url: String,
    currentDocumentId: Number
  }

  static targets = ["form", "items", "documentIdInput", "answeredVisibilityLabel", "saveBar"]

  connect() {
    this.refreshTimer = null
    this.inflight = false
    this.refreshQueued = false
    this.saveRequested = false
    this.hideAnswered = false
    this.onFormChange = this.onFormChange.bind(this)
    this.onSubmit = this.onSubmit.bind(this)

    if (this.hasFormTarget) {
      this.formTarget.addEventListener("change", this.onFormChange)
      this.formTarget.addEventListener("submit", this.onSubmit)
    }

    this.renderTabs()
    this.applyAnsweredVisibility()
  }

  disconnect() {
    if (this.hasFormTarget) {
      this.formTarget.removeEventListener("change", this.onFormChange)
      this.formTarget.removeEventListener("submit", this.onSubmit)
    }

    if (this.refreshTimer) clearTimeout(this.refreshTimer)
  }

  // Pressing Speichern while an autosave redraws the questionnaire replaced the button under the pointer, so the
  // click never happened (QA). The press is remembered and carried out once the redraw is done.
  rememberSave(event) {
    this.pressedSave = event.currentTarget
    // Released on the same button (a click, or dragged off on purpose): nothing to carry out later.
    document.addEventListener("pointerup", () => {
      if (this.pressedSave?.isConnected) this.pressedSave = null
    }, { once: true, capture: true })
  }

  // Speichern waits for a running or pending autosave, so its answers can't be overwritten by an older one.
  onSubmit(event) {
    this.saveRequested = false
    this.pressedSave = null
    if (!this.busy()) return

    event.preventDefault()
    this.saveRequested = true
    this.showSaving(true)
    this.flushRefresh()
  }

  // While Speichern waits for the autosave it already reads "Speichert…"; Turbo takes over once it submits.
  showSaving(waiting) {
    if (!this.hasFormTarget) return
    this.formTarget.querySelectorAll("[data-turbo-submits-with]").forEach((button) => {
      if (waiting && button.dataset.idleLabel === undefined) {
        button.dataset.idleLabel = button.innerHTML
        button.textContent = button.dataset.turboSubmitsWith
        button.disabled = true
      } else if (!waiting && button.dataset.idleLabel !== undefined) {
        button.innerHTML = button.dataset.idleLabel
        delete button.dataset.idleLabel
        button.disabled = false
      }
    })
  }

  busy() {
    return this.inflight || this.refreshTimer !== null || this.refreshQueued
  }

  flushRefresh() {
    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer)
      this.refreshTimer = null
      this.refreshRuntime()
    }
  }

  settled() {
    if (this.busy() || !this.hasFormTarget) return
    // A press whose button the redraw replaced never became a click.
    const lostPress = this.pressedSave && !this.pressedSave.isConnected
    if (!this.saveRequested && !lostPress) return

    this.saveRequested = false
    this.pressedSave = null
    this.showSaving(false)
    this.formTarget.requestSubmit()
  }

  switchDocument(event) {
    event.preventDefault()
    const nextId = Number(event.params.documentId)
    if (!nextId) return

    this.currentDocumentIdValue = nextId
    this.syncDocumentIdInput()
    this.renderTabs()
  }

  toggleAnsweredVisibility(event) {
    event.preventDefault()
    this.hideAnswered = !this.hideAnswered
    this.applyAnsweredVisibility()
  }

  onFormChange(event) {
    if (!this.isAnswerField(event.target)) return
    // Loading feedback while the refresh is in flight (the toggle itself flips via CSS).
    this.showSpinnerFor(event.target)
    this.scheduleRefresh({ debounced: false })
  }

  showSpinnerFor(target) {
    const item = target.closest(".js-questionnaire-item")
    const spinner = item?.querySelector(".js-questionnaire-spinner")
    if (spinner) spinner.classList.remove("hidden")
  }

  hideAllSpinners() {
    this.element
      .querySelectorAll(".js-questionnaire-spinner")
      .forEach((spinner) => spinner.classList.add("hidden"))
  }

  isAnswerField(target) {
    if (!target) return false
    const fieldName = target.getAttribute("name") || ""
    if (!fieldName.includes("[answer]")) return false

    // Only refresh for controls that affect progressive disclosure (select, radio, checkbox).
    // Text inputs don't influence condition evaluation, so skip them to avoid
    // replacing the DOM and losing the user's in-progress typing.
    const tag = target.tagName.toLowerCase()
    let isToggle = false
    if (tag === "input") {
      const type = (target.getAttribute("type") || "").toLowerCase()
      isToggle = type === "radio" || type === "checkbox"
    } else {
      isToggle = tag === "select"
    }
    if (!isToggle) return false

    // Variable answers don't influence condition evaluation / progressive disclosure,
    // so skip refresh to avoid replacing the DOM and losing the user's selection.
    const item = target.closest(".js-questionnaire-item")
    if (item) {
      const sourceType = item.querySelector('input[name*="[source_type]"]')
      if (sourceType && sourceType.value === "variable") return false
    }

    return true
  }

  scheduleRefresh({ debounced }) {
    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer)
      this.refreshTimer = null
    }

    if (!debounced) {
      this.refreshRuntime()
      return
    }

    this.refreshTimer = setTimeout(() => {
      this.refreshTimer = null
      this.refreshRuntime()
    }, 250)
  }

  serializeDraftRows() {
    if (!this.hasFormTarget) return []

    const rows = []
    this.formTarget.querySelectorAll(".js-questionnaire-item").forEach((item) => {
      const sourceTypeInput = item.querySelector('input[name*="[source_type]"]')
      const sourceIdInput = item.querySelector('input[name*="[source_id]"]')
      if (!sourceTypeInput || !sourceIdInput) return

      const checkboxInputs = item.querySelectorAll('input[type="checkbox"][name*="[answer]"]')
      const radioInputs = item.querySelectorAll('input[type="radio"][name*="[answer]"]')
      let answerValue = null

      if (checkboxInputs.length > 0) {
        answerValue = Array.from(checkboxInputs)
          .filter((input) => input.checked)
          .map((input) => input.value)
      } else if (radioInputs.length > 0) {
        const checkedRadio = Array.from(radioInputs).find((input) => input.checked)
        answerValue = checkedRadio ? checkedRadio.value : null
      } else {
        const answerInput = item.querySelector('select[name*="[answer]"], textarea[name*="[answer]"], input[name*="[answer]"]')
        answerValue = answerInput ? answerInput.value : null
      }

      // The explanation field is a <textarea> (see _questionary_item), so this must
      // not be scoped to hidden inputs — otherwise autosave sends an empty string
      // and blanks the user's explanation on the next progressive-disclosure refresh.
      const explanationInput = item.querySelector('textarea[name*="[explanation]"], input[name*="[explanation]"]')

      // Carry the current soft-lock state so a refresh preserves it: an item is
      // "persisted" (locked) unless the user has clicked Edit to unlock it
      // (which flips data-answer-locked to "false"). Without this, changing one
      // answer would knock every other saved answer back into edit state on the
      // re-render.
      const answerEditors = item.querySelectorAll('[data-template-questionnaire-item-target="answerEditor"]')
      const persisted = answerEditors.length > 0 &&
        Array.from(answerEditors).every((editor) => editor.dataset.answerLocked === "true")

      rows.push({
        source_type: sourceTypeInput.value,
        source_id: sourceIdInput.value,
        answer: answerValue,
        explanation: explanationInput ? explanationInput.value : "",
        persisted: persisted
      })
    })

    return rows
  }

  syncDocumentIdInput() {
    if (this.hasDocumentIdInputTarget) {
      this.documentIdInputTarget.value = this.currentDocumentIdValue || ""
    }
  }

  renderTabs() {
    const documentPanels = this.itemsTarget.querySelectorAll("[data-questionnaire-document-id]")
    const tabButtons = this.itemsTarget.querySelectorAll("[data-questionnaire-tab-id]")
    const availableIds = Array.from(documentPanels).map((panel) => Number(panel.dataset.questionnaireDocumentId))

    if (!availableIds.includes(this.currentDocumentIdValue)) {
      this.currentDocumentIdValue = availableIds[0] || 0
    }

    this.syncDocumentIdInput()

    documentPanels.forEach((panel) => {
      const active = Number(panel.dataset.questionnaireDocumentId) === this.currentDocumentIdValue
      panel.classList.toggle("hidden", !active)
      // No questions in the active document → nothing to save, hide the Save bar.
      if (active) {
        this.saveBarTargets.forEach((bar) => bar.classList.toggle("hidden", panel.dataset.questionnaireDocumentEmpty === "true"))
      }
    })

    tabButtons.forEach((button) => {
      const active = Number(button.dataset.questionnaireTabId) === this.currentDocumentIdValue
      const complete = button.dataset.questionnaireTabComplete === "true"
      const variant = active ? "active" : (complete ? "complete" : "inactive")

      const setState = (el, sets) => {
        if (!el) return
        el.classList.remove(...sets.active, ...(sets.complete || []), ...sets.inactive)
        el.classList.add(...(sets[variant] || sets.inactive))
      }

      setState(button, TAB_CARD)
      setState(button.querySelector("[data-questionnaire-tab-icon]"), TAB_ICON)
      setState(button.querySelector("[data-questionnaire-tab-label]"), TAB_LABEL)
      setState(button.querySelector("[data-questionnaire-tab-count]"), TAB_COUNT)
    })
  }

  applyAnsweredVisibility() {
    if (!this.hasItemsTarget) return

    const answeredItems = this.itemsTarget.querySelectorAll(".js-questionnaire-item")
    answeredItems.forEach((item) => {
      const answered = item.dataset.questionnaireAnswered === "true"
      item.classList.toggle("hidden", this.hideAnswered && answered)
    })

    this.answeredVisibilityLabelTargets.forEach((label) => {
      label.textContent = this.hideAnswered ? i18n.t("js.template_questionnaire.show_answered") : i18n.t("js.template_questionnaire.hide_answered")
    })
  }

  // One autosave at a time, in order: aborting an older one on the client did not stop it on the server, so it
  // could still land after a newer one and win. A change made while one is on its way queues the next.
  async refreshRuntime() {
    if (!this.urlValue || !this.hasItemsTarget) return
    if (this.inflight) {
      this.refreshQueued = true
      return
    }

    this.inflight = true
    const csrf = document.querySelector('meta[name="csrf-token"]')?.content || ""
    const rows = this.serializeDraftRows()
    const sent = JSON.stringify(rows)

    try {
      const response = await fetch(this.urlValue, {
        method: "POST",
        headers: {
          "Accept": "application/json",
          "Content-Type": "application/json",
          "X-CSRF-Token": csrf
        },
        credentials: "same-origin",
        body: JSON.stringify({
          document_id: this.currentDocumentIdValue,
          draft_answers_rows: rows
        })
      })

      if (!response.ok) {
        this.hideAllSpinners()
        window.boToast?.(i18n.t("js.template_questionnaire.autosave_failed"), "alert")
        return
      }

      const payload = await response.json()
      ;(payload.save_errors || []).forEach((message) => window.boToast?.(message, "alert"))
      if (typeof payload.items_html !== "string") {
        this.hideAllSpinners()
        return
      }

      // Changed meanwhile: drawing this answer would undo it; the queued autosave draws the latest.
      if (JSON.stringify(this.serializeDraftRows()) !== sent) {
        this.refreshQueued = true
        return
      }

      // The re-render brings spinners back hidden, so success needs no explicit clear.
      this.itemsTarget.innerHTML = payload.items_html
      if (payload.current_document_id) {
        this.currentDocumentIdValue = Number(payload.current_document_id)
      }
      this.renderTabs()
      this.applyAnsweredVisibility()
    } catch (error) {
      this.hideAllSpinners()
      window.boToast?.(i18n.t("js.template_questionnaire.autosave_failed"), "alert")
      console.error("Template questionnaire refresh failed", error)
    } finally {
      this.inflight = false
      if (this.refreshQueued) {
        this.refreshQueued = false
        this.refreshRuntime()
      } else {
        this.settled()
      }
    }
  }
}
